import { useCallback, useEffect, useRef } from 'react';

type UseEscapeKeyProps = {
  enabled?: boolean;
  eventTarget: Document | Window | HTMLElement;
  onKeydown?: (event: KeyboardEvent) => void;
  onKeyup?: (event: KeyboardEvent) => void;
  restoreFocusTarget?: HTMLElement | string;
};

/**
 * Custom React hook that listens for the Escape key press on a specified event target.
 * Supports both keydown and keyup events with separate callbacks, and automatically prevents
 * event propagation to avoid interference with parent components.
 *
 * @param enabled - Whether the Escape key listener is active. Defaults to `true`.
 * @param eventTarget - The target to listen for keyboard events (e.g., `document`, `window`, or an HTML element).
 * @param onKeydown - Optional callback function to execute when the Escape key is pressed down.
 * @param onKeyup - Optional callback function to execute when the Escape key is released.
 * @param restoreFocusTarget - Optional element or query selector string to restore focus to after Escape keyup event.
 */
export function useEscapeKey({
  enabled,
  eventTarget,
  onKeydown,
  onKeyup,
  restoreFocusTarget,
}: UseEscapeKeyProps) {
  const focusTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const clearFocusTimeout = () => {
    if (focusTimeoutRef.current) {
      clearTimeout(focusTimeoutRef.current);
    }
  };
  const restoreFocusToTarget = (target: HTMLElement | string) => {
    if (target) {
      const focusElement = typeof target === 'string' ? document.querySelector(target) : target;
      clearFocusTimeout();
      // Added setTimeout here to execute focus only after all DOM events have finished (ex: closing of modals)
      focusTimeoutRef.current = setTimeout(() => {
        if (focusElement instanceof HTMLElement) {
          focusElement?.focus();
        }
      });
    }
  };

  const handleEscKeyEvent = (event: Event, callbackFn?: (event: KeyboardEvent) => void) => {
    if (event instanceof KeyboardEvent && event.key === 'Escape' && callbackFn) {
      callbackFn(event);
    }
    event.stopPropagation();
  };

  const handleKeydown = useCallback(
    (event: Event) => {
      handleEscKeyEvent(event, onKeydown);
    },
    [onKeydown],
  );

  const handleKeyup = useCallback(
    (event: Event) => {
      handleEscKeyEvent(event, onKeyup);
      if (restoreFocusTarget) {
        restoreFocusToTarget(restoreFocusTarget);
      }
    },
    [onKeyup, restoreFocusTarget],
  );

  useEffect(() => {
    if (!enabled) {
      return;
    }
    eventTarget.addEventListener('keydown', handleKeydown);
    eventTarget.addEventListener('keyup', handleKeyup);
    return () => {
      clearFocusTimeout();
      eventTarget.removeEventListener('keydown', handleKeydown);
      eventTarget.removeEventListener('keyup', handleKeyup);
    };
  }, [enabled, eventTarget, handleKeydown, handleKeyup]);
}
